/**
 *
 * new submissions list for PolicyCenter ALP quotes
 *
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  formatDate,
  FormatDate,
  Table,
  Form,
  FormGroup,
  Input,
  useForm,
  Currency,
  LoadingIndicator,
  FlexRow
} from "@ufginsurance/ui-kit";
import { slugify, toTitleCase, ellipsisTruncate } from "../../Factory";
import classnames from "classnames";
import { submissionList } from "../../../services/commercialService";
import { useGetData } from "../../../online-quoting/shared/useGetData";
import { useHistory } from "react-router-dom";
import { useNewQuoteButton } from "./useNewQuoteButton";
import SubmissionsListActions from "./SubmissionsListActions";

import "./SubmissionsList.scss";
import HelpDeskMessage from "../../../help-desk/helpDeskMessage";

const SubmissionsList = () => {
  const history = useHistory();
  const form = useForm({ values: { search: "" }, onSubmit: () => {} });
  const { handleOnBlur, handleOnValidate, values, handleOnChange } = form;

  const [quotes, setQuotes] = useState([]);
  const [page, setPage] = useState(1);
  // loads quote data... will reload if dependencies change (like page)
  const {
    data: tableData,
    loading,
    error
  } = useGetData({
    apiCall: ({ options }) => submissionList({ page, options }),
    deps: [submissionList, page],
    cancelOnUnmount: true
  });

  const { data, totalCount, pageSize } = tableData || {};

  const modifiedQuotesRef = useRef({});

  /**
   * when a quote is modified (like withdrawn), update the quote in state
   */
  useEffect(() => {
    // if modifiedQuotesRef has changed, update the quotes found in it
    if (modifiedQuotesRef.current) {
      const updatedQuotes = Object.values(modifiedQuotesRef.current);
      updatedQuotes.forEach(quote => {
        const index = quotes.findIndex(
          q => q.referenceId === quote.referenceId
        );
        if (index !== -1) quotes[index] = quote;
      });
      setQuotes(quotes);
    }
  }, [modifiedQuotesRef, quotes]);

  /**
   * when the data changes, update the quotes, keeping the original quotes adding
   * the new quotes to the end of the array, checking for duplicates
   * comparing quotes and data by referenceId (ignoring modifiedQuotesRef)
   */
  useEffect(() => {
    if (data && data.length > 0) {
      // Create new array with existing quotes if any
      const newQuotes = quotes ? [...quotes] : [];
      // Add new quotes from data, checking for duplicates
      data.forEach(quote => {
        const exists = newQuotes.some(q => q.referenceId === quote.referenceId);
        if (!exists) newQuotes.push(quote);
      });
      if (newQuotes?.length !== quotes?.length) setQuotes(newQuotes);
    }
  }, [data, quotes]);

  const loadNextPage = () => setPage(page + 1);
  const isReviewDisabled = ({ errorOnPC, status }) =>
    errorOnPC || ["Declined", "Not Taken"].includes(status);

  const columns = [
    {
      label: "Account",
      sortable: true,
      element: ({ displayName, primaryAddress }) => {
        const maxLength = 25;
        const tooLong = displayName?.length > maxLength;
        return (
          <div className="oq__submissions-list__account__container">
            <div
              className="oq__submissions-list__account"
              data-tip={tooLong ? displayName : null}
              role={tooLong ? "button" : "none"}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={tooLong ? "0" : "-1"}
            >
              {ellipsisTruncate(displayName, maxLength)}
            </div>
            <div className="oq__submissions-list__account-address">
              {toTitleCase(primaryAddress.city)}, {primaryAddress.state}
            </div>
          </div>
        );
      },
      sortCompare: (a, b) => {
        const aName = a.displayName;
        const bName = b.displayName;
        return aName === bName ? b.id - a.id : aName - bName;
      }
    },
    {
      label: "Account Number",
      key: "accountNumber",
      sortable: true
    },
    {
      label: "Effective Date",
      element: row => <FormatDate date={row.effectiveDate} format="M/D/YYYY" />,
      sortable: true,
      sortCompare: (a, b) => {
        const aDate = formatDate(a.effectiveDate);
        const bDate = formatDate(b.effectiveDate);
        return aDate === bDate ? b.id - a.id : aDate - bDate;
      }
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      element: row => (
        <span className="oq__submissions-list__status-container">
          <span
            className={classnames(
              "oq__submissions-list__status-dot",
              `oq__submissions-list__status-dot__${slugify(row.status)}`
            )}
          ></span>
          <span className="oq__submissions-list__status-text">
            {row.status}
          </span>
        </span>
      )
    },

    {
      key: "premium",
      label: "Premium",
      sortable: true,
      align: "right",
      element: row => {
        //if any of the linesofBusiness are in draft... show dash for premium.
        const lobs = row?.linesOfBusiness || [];
        if (lobs.length === 0 || lobs.some(l => l.status === "Draft")) {
          return "-";
        }
        return row?.premium === 0 ? "-" : <Currency amount={row.premium} />;
      }
    },
    {
      label: "",
      element: row => (
        <Button
          inline
          isLink
          onClick={() => history.push(`/proquote/loader/${row.referenceId}`)}
          disabled={isReviewDisabled(row)}
        >
          Review Quote
        </Button>
      )
    },
    {
      label: "",
      element: row =>
        isReviewDisabled(row) ? null : (
          <SubmissionsListActions
            quote={row}
            afterWithdraw={async ({ changedQuote }) => {
              /**
               * after a successfull withdraw,
               * update these fields for the submission that matches the referenceId
               * withdrawn: true
               * status: "Not Taken"
               */
              const newData = quotes.map(quote =>
                changedQuote.referenceId === quote.referenceId
                  ? changedQuote
                  : quote
              );
              modifiedQuotesRef.current[changedQuote.referenceId] =
                changedQuote;

              setQuotes(newData);
            }}
          />
        )
    }
  ];

  const { NewQuoteButton, QuotingModal } = useNewQuoteButton();

  if (loading) return <LoadingIndicator />;

  return (
    <div>
      <QuotingModal />
      <FlexRow>
        <Form context={form} className="oq__submissions-list__form">
          <FormGroup>
            <Input
              id="search"
              name="search"
              onBlur={handleOnBlur}
              onValidate={handleOnValidate}
              label="Search Table"
              onChange={handleOnChange}
              value={values.search}
            />
            {!loading && totalCount > quotes.length && (
              <div className="oq__submissions-list__load-more">
                <div className="oq__submissions-list__load-more__inner">
                  <span className="oq__submissions-list__load-more__inner__text">
                    {totalCount - quotes.length} more submissions available
                  </span>
                  <Button variant="outline" onClick={loadNextPage}>
                    Load More submissions
                  </Button>
                </div>
              </div>
            )}
            {loading && (
              <div className="oq__submissions-list__paging-loading">
                <LoadingIndicator
                  message={`Loading ${pageSize} more submissions...`}
                />
              </div>
            )}
            {error && quotes?.length > 0 && (
              <div className="oq__submissions-list__paging-error">
                Error loading submissions (page: {page} of{" "}
                {Math.ceil(totalCount / pageSize)}): {error.message}
              </div>
            )}
          </FormGroup>
        </Form>
        <div className="oq__submissions-list__new-quote">
          <NewQuoteButton />
        </div>
      </FlexRow>

      {quotes && (
        <Table
          id="oq__submissions-list"
          className="oq__submissions-list"
          rowKey="referenceId"
          columns={columns}
          data={quotes}
          showPagination
          itemsPerPage={100}
          searchQuery={values.search}
          initialSort="effectiveDate"
          initialSortDirection="desc"
        />
      )}
      {error && (!quotes || quotes?.length === 0) && (
        <div className="oq__submissions-list__error">
          <HelpDeskMessage display={"anErrorOccurred"} />
        </div>
      )}
    </div>
  );
};

export default SubmissionsList;
